import { useEffect, useRef } from "react";
import strip from "../../../images/royal-mahal/bird-flight.webp";
import { prefersReducedMotion } from "../hooks";
import { FRAMES, createBird, facingOf, frameOf, launch, step } from "./flight";

// Sizes are relative to the flight band's width; the farther bird is smaller, and since
// its flight is worked out in body lengths it also crosses the sky more slowly.
const FLOCK = [0.045, 0.036, 0.026];
const MIN_PX = [34, 28, 22];
const MAX_PX = [58, 48, 38];
const ASPECT = 122 / 128; // sprite frame height / width

const rand = (min, max) => min + Math.random() * (max - min);

/**
 * Two or three small birds flying unhurriedly about the layer's stretch of sky in
 * bounding flight (see art/flight.js): a few wingbeats, a coast with wings folded,
 * and on, swinging round before they reach the edge of the view.
 */
const Birds = ({ className = "", count = 3 }) => {
  const layer = useRef(null);
  const els = useRef([]);

  useEffect(() => {
    const area = layer.current;
    if (!area) return undefined;
    const sizes = FLOCK.slice(0, count);
    const bounds = { w: area.clientWidth, h: area.clientHeight };
    // ...and never so big that its rise and fall would outgrow a short stretch of sky
    const sizePx = (i) =>
      Math.min(MAX_PX[i], Math.max(MIN_PX[i], bounds.w * sizes[i]), bounds.h * 0.3 * (sizes[i] / sizes[0]));
    const birds = sizes.map((_, i) => createBird(sizePx(i)));

    const draw = (b, i) => {
      const el = els.current[i];
      if (!el) return;
      el.style.opacity = "1";
      const h = b.L * ASPECT;
      el.style.width = `${b.L}px`;
      el.style.transform = `translate3d(${(b.x - b.L / 2).toFixed(1)}px, ${(b.y - h / 2).toFixed(1)}px, 0)`;
      const sprite = el.firstChild;
      sprite.style.transform = `scaleX(${facingOf(b).toFixed(3)}) rotate(${b.pitch.toFixed(3)}rad)`;
      sprite.style.backgroundPositionX = `${(frameOf(b) / (FRAMES - 1)) * 100}%`;
    };

    // All in flight from the start, spread across the sky
    birds.forEach((b, i) => {
      const slot = (i + rand(0.2, 0.8)) / birds.length;
      launch(b, bounds, { x: (0.1 + 0.8 * slot) * bounds.w });
      draw(b, i);
    });
    if (prefersReducedMotion()) return undefined;

    // The stretch of sky can change size (window resized, the title above it reflowing)
    const onResize = () => {
      bounds.w = area.clientWidth;
      bounds.h = area.clientHeight;
      birds.forEach((b, i) => {
        b.L = sizePx(i);
      });
    };
    const resizer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(onResize) : null;
    if (resizer) resizer.observe(area);
    else window.addEventListener("resize", onResize);

    let frame = 0;
    let last = 0;
    const tick = (now) => {
      const dt = last ? Math.min(0.05, (now - last) / 1000) : 1 / 60;
      last = now;
      birds.forEach((b, i) => {
        step(b, dt, bounds);
        draw(b, i);
      });
      frame = requestAnimationFrame(tick);
    };

    // Only fly while the sky is on screen and the tab is open
    let visible = false;
    const run = () => {
      if (frame || !visible || document.hidden) return;
      last = 0;
      frame = requestAnimationFrame(tick);
    };
    const pause = () => {
      cancelAnimationFrame(frame);
      frame = 0;
    };
    const observer =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(([entry]) => {
            visible = entry.isIntersecting;
            if (visible) run();
            else pause();
          })
        : null;
    if (observer) observer.observe(area);
    else {
      visible = true;
      run();
    }
    const onVisibility = () => (document.hidden ? pause() : run());
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      pause();
      observer?.disconnect();
      resizer?.disconnect();
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [count]);

  return (
    <div ref={layer} className={`rm-flyers ${className}`} aria-hidden="true">
      {FLOCK.slice(0, count).map((_, i) => (
        <div key={i} ref={(el) => (els.current[i] = el)} className="rm-flyer">
          <div className="rm-flyer__sprite" style={{ backgroundImage: `url(${strip})` }} />
        </div>
      ))}
    </div>
  );
};

export default Birds;
